import LoadingTextSkeleton from '@components/skeleton/LoadingTextSkeleton';
import { cameraOverviewHttpController } from '@controllers/index';
import { ICameraDailyWorkedHour, IGetCameraPingHour } from '@EcamModel/controllers/ICameraOverviewHttpController';
import { CameraBatteryDetail, ConsolidateCameraMonitoring } from '@EcamModel/model/CameraOverview';
import { CameraTask } from '@EcamModel/model/CameraTask';
import { CircularProgress, Stack, Typography } from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import { DetailsInformation } from '@pages/cameras/details/components/CameraInformation';
import { BarChart } from '@pages/overview/charts/BarChartCameraStatus';
import { ChartItem } from '@pages/overview/components/PopUpAvgWorkedHours';
import color from '@theme/Colors';
import { useQuery } from 'react-query';
import { endOfStartDate, formatActivateTime, formatUKTimeDate, startOfThreeDaysAgo } from 'src/helpers';
import ScrollXBarStackedChart from '../ScrollXBarStackedChart';

export type TabCheckedProps = {
    taskItem: CameraTask;
    cameraConfigMonitoring?: ConsolidateCameraMonitoring[];
    loadingCameraConfigMonitoring: boolean;
};

export default function TabCheckBatteryHealth({
    taskItem,
    cameraConfigMonitoring,
    loadingCameraConfigMonitoring,
}: TabCheckedProps) {
    const getBatteryDetailByHourly = async () => {
        const _filterBattery: ICameraDailyWorkedHour = {
            CameraId: taskItem.CameraId,
            From: startOfThreeDaysAgo,
            To: endOfStartDate,
        };

        const response = await cameraOverviewHttpController.cameraBatteryDetail(_filterBattery);
        return response;
    };

    const {
        data: cameraDailyBatteryDetail,
        error: errorCameraDailyBatteryDetail,
        isLoading: loadingCameraDailyBatteryDetail,
    } = useQuery(['cameraDailyBatteryDetail', taskItem.Id, taskItem.CameraId], getBatteryDetailByHourly, {
        staleTime: 20 * 60 * 1000,
        refetchOnWindowFocus: false,
        enabled: !!taskItem.Id,
    });

    const cameraBatteryDetailPopup = ((cameraDailyBatteryDetail as CameraBatteryDetail[]) ?? []).map((i) => {
        let chart: BarChart = { label: formatUKTimeDate(i.Created), value: i.Battery };
        return chart;
    });

    const getPingStatus = async () => {
        const _filterPing: IGetCameraPingHour = {
            CameraId: taskItem.CameraId,
            From: startOfThreeDaysAgo,
            To: endOfStartDate,
        };
        const response = await cameraOverviewHttpController.cameraPingDetail(_filterPing);
        return response;
    };

    const {
        data: cameraPingStatus,
        error: errorCameraPingStatus,
        isLoading: loadingCameraPingStatus,
    } = useQuery(['cameraPingStatus', taskItem.Id, taskItem.CameraId], getPingStatus, {
        staleTime: 20 * 60 * 1000,
        refetchOnWindowFocus: false,
        enabled: !!taskItem.Id,
    });

    const detailBatteryHealth: DetailsInformation[] = [
        {
            id: 1,
            label: 'Last contract:',
            value: (
                <LoadingTextSkeleton
                    conditions={loadingCameraConfigMonitoring}
                    value={
                        cameraConfigMonitoring?.[0]?.LastContact
                            ? formatActivateTime(cameraConfigMonitoring[0].LastContact)
                            : '-'
                    }
                    loadingStyle={{
                        width: '50%',
                    }}
                />
            ),
        },
        {
            id: 2,
            label: 'Uptime (h):',
            value: (
                <LoadingTextSkeleton
                    conditions={loadingCameraConfigMonitoring}
                    value={cameraConfigMonitoring?.[0]?.AvgDailyWorkedHours ?? 'N/A'}
                    loadingStyle={{
                        width: '50%',
                    }}
                />
            ),
        },
        {
            id: 3,
            label: 'Latest battery:',
            value: (
                <LoadingTextSkeleton
                    conditions={loadingCameraConfigMonitoring}
                    value={
                        cameraConfigMonitoring?.[0]?.CurrentBattery
                            ? `${cameraConfigMonitoring?.[0]?.CurrentBattery}%`
                            : null
                    }
                    loadingStyle={{
                        width: '50%',
                    }}
                />
            ),
        },
        {
            id: 4,
            label: 'Average % battery:',
            value: (
                <Typography variant="body1">
                    <LoadingTextSkeleton
                        conditions={loadingCameraConfigMonitoring}
                        value={
                            cameraConfigMonitoring?.[0]?.AvgBattery
                                ? `${parseFloat(cameraConfigMonitoring?.[0]?.AvgBattery.toFixed(2))}%`
                                : null
                        }
                        loadingStyle={{
                            width: '50%',
                        }}
                    />
                </Typography>
            ),
        },
    ];

    console.log('cameraPingStatus?.length', cameraPingStatus?.length);

    return (
        <Stack direction={'column'} spacing={3} bgcolor={color.grey100} borderRadius={'8px'} p={2}>
            <Stack direction={'column'} spacing={1}>
                <Typography variant="h6" color={color.success}>
                    Information
                </Typography>
                <Stack
                    width={'100%'}
                    padding={2}
                    bgcolor={color.white}
                    borderRadius={'4px'}
                    sx={{
                        maxHeight: '35vh',
                        overflowY: 'auto',
                        '::-webkit-scrollbar': {
                            width: '4px',
                        },
                    }}
                >
                    <Grid2 container spacing={2}>
                        {detailBatteryHealth.map((item) => (
                            <Grid2 xs={6} key={item.id}>
                                <Stack direction={'column'} spacing={1}>
                                    <Stack
                                        key={item.id}
                                        direction={'row'}
                                        width={'100%'}
                                        justifyContent={'space-between'}
                                    >
                                        <Stack width={'50%'}>
                                            <Typography variant="h6">{item.label}</Typography>
                                        </Stack>
                                        <Stack width={'50%'}>
                                            <Typography variant="body1">{item.value}</Typography>
                                        </Stack>
                                    </Stack>
                                </Stack>
                            </Grid2>
                        ))}
                    </Grid2>
                </Stack>
            </Stack>

            <Stack direction={'column'} spacing={1}>
                <Typography variant="h6" color={color.success}>
                    Internal battery detail (last 3 days)
                </Typography>
                <Stack
                    width={'100%'}
                    padding={2}
                    bgcolor={color.white}
                    borderRadius={'4px'}
                    sx={{
                        maxHeight: '35vh',
                        overflowY: 'auto',
                        '::-webkit-scrollbar': {
                            width: '4px',
                        },
                    }}
                >
                    <Stack gap={2}>
                        {loadingCameraDailyBatteryDetail ? (
                            <Stack justifyContent="center" alignItems="center" minHeight={300} bgcolor={color.grey100}>
                                <CircularProgress size={30} />
                            </Stack>
                        ) : (
                            <ChartItem
                                dataBattery={true}
                                dataAxisY={false}
                                title={<Typography variant="h5">{`%Battery`}</Typography>}
                                chartColor={color.secondary}
                                data={cameraBatteryDetailPopup}
                                label="Number of internal battery detail"
                            />
                        )}

                        {loadingCameraPingStatus ? (
                            <Stack justifyContent="center" alignItems="center" minHeight={300} bgcolor={color.grey100}>
                                <CircularProgress size={30} />
                            </Stack>
                        ) : (
                            <ScrollXBarStackedChart data={cameraPingStatus ?? []} />
                        )}
                    </Stack>
                </Stack>
            </Stack>
        </Stack>
    );
}
